import { useState, useCallback, useRef, type ReactElement } from 'react';
import {
  useAtomValue as useStateSegmentValue,
  useSetAtom as useStateSegmentSetter,
} from 'jotai';
import { emitCustomEvent } from 'react-custom-events';
import { PAGE_ROUTES } from '../constants/common';
import { translate } from '../localization';
import Modal from '../components/molecules/Modal';
import { contentListStateSegment } from '../store/markettingContentGeneration/stateSegments/contentListSegment';
import { moduleContentsStateSegment } from '../store/markettingContentGeneration/stateSegments/emailModulesStateSegment';
import { agenticSessionSegment } from '../store/globalStore/stateSegments/agenticSessionSegment';
import { deleteAgenticSession } from '../services/AgenticSession';

/** Modal types for navigation guards */
type ModalType = 'start-new-session' | null;

/** Agentic sub-routes that indicate an active session */
const AGENTIC_SESSION_ROUTES = ['/create-plan', '/generate-content'];

/** Base path for agentic planner (without /home suffix) */
const AGENTIC_PLANNER_BASE = '/agentic-planner';

export interface UseAgenticNavigationGuardReturn {
  /** Handle navigation with guard check for agentic planner routes */
  handleNavigationWithGuard: (
    destination: string,
    onNavigate: (destination: string) => void
  ) => void;
  /** Render the navigation modal if open */
  renderNavigationModal: () => ReactElement | null;
  /** Whether a modal is currently open */
  isModalOpen: boolean;
}

/**
 * Hook that provides navigation guards for the agentic planner.
 *
 * Uses route-based detection to determine if user has an active session:
 * - If on /agentic-planner/create-plan or /agentic-planner/generate-content,
 *   user is considered to have an active session.
 *
 * Shows modal when:
 * - User is on create-plan/generate-content and clicks agentic planner icon
 *   → "Start New Session?" modal
 *
 * Navigation to other routes is allowed without confirmation since session
 * recovery enables users to return to their previous session.
 *
 * Note: Uses window.location.pathname instead of React Router's location
 * because the host app's router doesn't track sub-routes within the remote app.
 */
export function useAgenticNavigationGuard(): UseAgenticNavigationGuardReturn {
  const [modalType, setModalType] = useState<ModalType>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Access content state to check for existing content
  const { contentList: contentItems } = useStateSegmentValue(
    contentListStateSegment
  );
  const moduleContents = useStateSegmentValue(moduleContentsStateSegment);

  // Setter to clear the cached agentic session when starting fresh
  const setAgenticSession = useStateSegmentSetter(agenticSessionSegment);

  // Store pending navigation info
  const pendingNavigation = useRef<{
    destination: string;
    onNavigate: (destination: string) => void;
  } | null>(null);

  /**
   * Get current pathname from browser URL
   * Using window.location because React Router in host doesn't track remote sub-routes
   */
  const getCurrentPathname = useCallback(() => {
    return window.location.pathname;
  }, []);

  /**
   * Check if current route is the generate-content route (non-agentic)
   */
  const isOnGenerateContent = useCallback(() => {
    const pathname = getCurrentPathname();
    return (
      pathname === PAGE_ROUTES.GENERATE_CONTENT ||
      pathname.startsWith(PAGE_ROUTES.GENERATE_CONTENT + '/')
    );
  }, [getCurrentPathname]);

  /**
   * Check if there is existing content that would be lost
   * Returns true if contentList has items OR email modules have been configured
   */
  const hasExistingContent = useCallback(() => {
    return contentItems.length > 0 || moduleContents.size > 0;
  }, [contentItems, moduleContents]);

  /**
   * Check if current route is within the agentic planner
   */
  const isOnAgenticPlanner = useCallback(() => {
    return getCurrentPathname().startsWith(AGENTIC_PLANNER_BASE);
  }, [getCurrentPathname]);

  /**
   * Check if user is on a route that indicates an active session
   * (create-plan or generate-content)
   */
  const isOnActiveSessionRoute = useCallback(() => {
    const pathname = getCurrentPathname();
    return AGENTIC_SESSION_ROUTES.some(route => pathname.includes(route));
  }, [getCurrentPathname]);

  /**
   * Extract session ID from URL path
   * Matches: /agentic-planner/create-plan/{sessionId} or /agentic-planner/generate-content/{sessionId}
   */
  const extractSessionIdFromUrl = useCallback(() => {
    const pathname = getCurrentPathname();
    const match = pathname.match(
      /\/agentic-planner\/(?:create-plan|generate-content)\/([^/?]+)/
    );
    return match ? decodeURIComponent(match[1]) : null;
  }, [getCurrentPathname]);

  /**
   * Handle navigation with guard checks
   */
  const handleNavigationWithGuard = useCallback(
    (destination: string, onNavigate: (destination: string) => void) => {
      const currentPath = getCurrentPathname();
      const onGenerateContent = isOnGenerateContent();
      const existingContent = hasExistingContent();

      console.log('[NavigationGuard] handleNavigationWithGuard called', {
        destination,
        currentPath,
        isOnAgenticPlanner: isOnAgenticPlanner(),
        isOnActiveSessionRoute: isOnActiveSessionRoute(),
        isOnGenerateContent: onGenerateContent,
        hasExistingContent: existingContent,
      });

      // Check for generate-content route with existing content
      // When user clicks "Create" while already on generate-content with existing content
      // Emit 'start-new' event directly - MarketingPanel will show its modal
      if (
        onGenerateContent &&
        destination === PAGE_ROUTES.GENERATE_CONTENT &&
        existingContent
      ) {
        console.log(
          '[NavigationGuard] On generate-content with existing content, emitting start-new event'
        );
        emitCustomEvent('start-new');
        return;
      }

      // If not on agentic planner, navigate directly
      if (!isOnAgenticPlanner()) {
        console.log(
          '[NavigationGuard] Not on agentic planner, navigating directly'
        );
        onNavigate(destination);
        return;
      }

      // If not on an active session route, navigate directly
      if (!isOnActiveSessionRoute()) {
        console.log(
          '[NavigationGuard] Not on active session route, navigating directly'
        );
        onNavigate(destination);
        return;
      }

      // User is on create-plan or generate-content
      const isNavigatingToAgenticHome =
        destination === PAGE_ROUTES.AGENTIC_PLANNER;

      // If navigating to other routes, store session ID for recovery and allow navigation
      if (!isNavigatingToAgenticHome) {
        const sessionId = extractSessionIdFromUrl();
        if (sessionId) {
          console.log(
            '[NavigationGuard] Storing session ID before navigating away:',
            sessionId
          );
          setAgenticSession({ chatSessionId: sessionId, hasChecked: true });
        }
        console.log(
          '[NavigationGuard] Navigating away from agentic planner, session will be recoverable'
        );
        onNavigate(destination);
        return;
      }

      // User is clicking agentic planner icon while on create-plan/generate-content
      // Show "Start New Session?" modal
      console.log('[NavigationGuard] Showing start-new-session modal');
      pendingNavigation.current = { destination, onNavigate };
      setModalType('start-new-session');
    },
    [
      isOnAgenticPlanner,
      isOnActiveSessionRoute,
      isOnGenerateContent,
      hasExistingContent,
      getCurrentPathname,
      extractSessionIdFromUrl,
      setAgenticSession,
    ]
  );

  /**
   * Handle modal cancel - close modal and stay on page
   */
  const handleCancel = useCallback(() => {
    setModalType(null);
    pendingNavigation.current = null;
  }, []);

  /**
   * Handle modal continue action (start new session - for agentic planner)
   * Extracts session_id from URL and deletes the session before reloading
   */
  const handleContinue = useCallback(async () => {
    if (!pendingNavigation.current) return;

    setIsDeleting(true);

    try {
      // Extract session_id from current URL path
      const sessionId = extractSessionIdFromUrl();

      if (sessionId) {
        console.log('[NavigationGuard] Deleting session from URL:', sessionId);
        const deleted = await deleteAgenticSession(sessionId);

        if (!deleted) {
          console.warn(
            '[NavigationGuard] Failed to delete session:',
            sessionId
          );
        }

        // Clear the cached session in global state
        setAgenticSession({ chatSessionId: null, hasChecked: true });
      } else {
        console.log('[NavigationGuard] No session ID found in URL');
      }
    } catch (error) {
      console.error('[NavigationGuard] Error during session deletion:', error);
    } finally {
      setIsDeleting(false);
      setModalType(null);
      pendingNavigation.current = null;

      // Dispatch native DOM event for remote app to reset its state and navigate to home
      // Using native CustomEvent because eventBus isn't shared across module federation
      window.dispatchEvent(new CustomEvent('agentic-session-reset'));
    }
  }, [extractSessionIdFromUrl, setAgenticSession]);

  /**
   * Render the navigation modal for agentic planner
   */
  const renderNavigationModal = useCallback(() => {
    console.log(
      '[NavigationGuard] renderNavigationModal called, modalType:',
      modalType
    );
    if (!modalType) return null;

    return (
      <Modal
        title={translate('agentic_start_new_session_title')}
        description={translate('agentic_start_new_session_message')}
        secondaryCTALabel={translate('common_cancel')}
        primaryCTALabel={translate('common_continue')}
        onSecondaryCTAClick={handleCancel}
        onPrimaryCTAClick={handleContinue}
        onClose={handleCancel}
        isDisabled={isDeleting}
        showSpinner={isDeleting}
      />
    );
  }, [modalType, handleCancel, handleContinue, isDeleting]);

  return {
    handleNavigationWithGuard,
    renderNavigationModal,
    isModalOpen: modalType !== null,
  };
}
